import { Duration } from 'luxon'
import type { DateTime } from 'luxon'

// The "Where is my match?" triage script for the Valve (Steam) tab: the
// questions support asks when a user can't find a match, in the order they ask
// them. Both questions (when, then mode) come before any ending, so an old
// Casual match gets the mode answer rather than "expired".

export enum WhenRange {
  Under30Minutes = 'under_30m',
  Under1Day = '30m_1d',
  Days1To3 = '1_3d',
  Days3To6 = '3_6d',
  Days7To14 = '7_14d',
  Over14Days = 'over_14d'
}

export const whenRanges = Object.values(WhenRange)

// The newest edge of each range: how long ago the match finished at the latest.
const newestEdge: Record<WhenRange, Duration> = {
  [WhenRange.Under30Minutes]: Duration.fromObject({ minutes: 0 }),
  [WhenRange.Under1Day]: Duration.fromObject({ minutes: 30 }),
  [WhenRange.Days1To3]: Duration.fromObject({ days: 1 }),
  [WhenRange.Days3To6]: Duration.fromObject({ days: 3 }),
  [WhenRange.Days7To14]: Duration.fromObject({ days: 7 }),
  [WhenRange.Over14Days]: Duration.fromObject({ days: 14 })
}

// Valve deletes demos after 7 days (matches:error_modal:demo_not_found).
const expiredRanges = new Set([WhenRange.Days7To14, WhenRange.Over14Days])

export enum Mode {
  Premier = 'premier',
  Competitive = 'competitive',
  Wingman = 'wingman',
  Rush = 'rush',
  Casual = 'casual',
  Deathmatch = 'deathmatch',
  ArmsRace = 'arms_race',
  Retakes = 'retakes',
  OtherOfficial = 'other_official',
  Faceit = 'faceit',
  Esportal = 'esportal',
  PopFlash = 'popflash',
  Renown = 'renown',
  GamersClub = 'gamers_club',
  FiveEArena = '5e_arena',
  PerfectWorld = 'perfect_world',
  Csc = 'csc',
  PrivateMatchmaking = 'private_matchmaking',
  CommunityServer = 'community_server',
  SomewhereElse = 'somewhere_else'
}

// Same order as the tabs under Play > Matchmaking in CS2.
export const officialModes = [
  Mode.Premier,
  Mode.Competitive,
  Mode.Wingman,
  Mode.Rush,
  Mode.Casual,
  Mode.Deathmatch,
  Mode.ArmsRace,
  Mode.Retakes,
  Mode.OtherOfficial
]

export const elsewhereModes = [
  Mode.Faceit,
  Mode.Esportal,
  Mode.PopFlash,
  Mode.Renown,
  Mode.GamersClub,
  Mode.FiveEArena,
  Mode.PerfectWorld,
  Mode.Csc,
  Mode.PrivateMatchmaking,
  Mode.CommunityServer,
  Mode.SomewhereElse
]

// Valve only hands out share codes for these.
const downloadableModes = new Set([
  Mode.Premier,
  Mode.Competitive,
  Mode.Wingman
])

export enum AccountAnswer {
  Yes = 'yes',
  No = 'no'
}

export enum BadgeAnswer {
  Error = 'error',
  InProgress = 'in_progress',
  None = 'none'
}

export enum Question {
  When = 'when',
  Mode = 'mode',
  Account = 'account',
  Badge = 'badge'
}

export enum Outcome {
  AuthCodeMissing = 'auth_code_missing',
  UnsupportedMode = 'unsupported_mode',
  Faceit = 'faceit',
  OtherPlatform = 'other_platform',
  TooRecent = 'too_recent',
  Expired = 'expired',
  BeforeJoined = 'before_joined',
  OtherAccount = 'other_account',
  HasError = 'has_error',
  InProgress = 'in_progress',
  Support = 'support'
}

export type Answers = {
  when?: WhenRange
  mode?: Mode
  account?: AccountAnswer
  badge?: BadgeAnswer
}

export type Context = {
  autoDownloadActive: boolean
  accountCreatedAt: DateTime | null
  now: DateTime
}

export type Step =
  | { kind: 'question'; question: Question }
  | { kind: 'outcome'; outcome: Outcome }

const ask = (question: Question): Step => ({ kind: 'question', question })
const end = (outcome: Outcome): Step => ({ kind: 'outcome', outcome })

export function nextStep(answers: Answers, context: Context): Step {
  if (!context.autoDownloadActive) return end(Outcome.AuthCodeMissing)

  const { when, mode } = answers
  if (!when) return ask(Question.When)
  if (!mode) return ask(Question.Mode)

  if (mode === Mode.Faceit) return end(Outcome.Faceit)
  if (elsewhereModes.includes(mode)) return end(Outcome.OtherPlatform)
  if (!downloadableModes.has(mode)) return end(Outcome.UnsupportedMode)

  if (when === WhenRange.Under30Minutes) return end(Outcome.TooRecent)
  if (expiredRanges.has(when)) return end(Outcome.Expired)

  if (playedBeforeJoining(when, context)) return end(Outcome.BeforeJoined)

  if (!answers.account) return ask(Question.Account)
  if (answers.account === AccountAnswer.No) return end(Outcome.OtherAccount)

  if (!answers.badge) return ask(Question.Badge)
  if (answers.badge === BadgeAnswer.Error) return end(Outcome.HasError)
  if (answers.badge === BadgeAnswer.InProgress) return end(Outcome.InProgress)

  return end(Outcome.Support)
}

function playedBeforeJoining(when: WhenRange, context: Context) {
  if (!context.accountCreatedAt) return false
  const latestPlayedAt = context.now.minus(newestEdge[when])
  return latestPlayedAt < context.accountCreatedAt
}
